"use client"

import React, { useEffect, useState, useMemo, useCallback, useRef } from "react"
import { useLocation, useNavigate, Link } from "react-router-dom"
import SimpleBar from "simplebar-react"
import "simplebar/dist/simplebar.min.css"
import {
  Users,
  Share2,
  MonitorIcon,
  Headset,
  LogIn,
  BarChart2,
  ShoppingCart,
  UserPlus,
  CreditCard,
  FileText,
  LogOut,
  Activity,
  ChevronUp,
  ChevronDown,
  LayoutDashboard,
  DollarSign,
  X,
  Bell,
  Settings,
} from "lucide-react"
import {
  SidebarOptionSuperAdmin,
  DashboardMenuClient,
  DashboardMenuExpert,
  DashboardMenuManager,
  DashboardMenuOperator,
  SidebarOptionAdmin,
  SidebarOptionMarketing,
} from "../../utils/LayoutConstant"
import { useDispatch, useSelector } from "react-redux"
import { checkLogin } from "../../utils/functions/functions"
import { Whatsapp } from "react-bootstrap-icons"
import NotificationList from "../notification/component/NotificationList"
import CustomModal from "../../utils/modals/CustomModal"
import FormCallHippoPermission from "../notification/component/FormCallHippoPermission"
import Logout from "../../utils/Logout"
import { closeChatAction, getNotificationListAction } from "../../../services/action/common"
import { handlePopupModalAction } from "../../../services/action/event"
import { logoutAction } from "../../../services/action/authentication"

// Add CSS for ripple effect and other styles
const customStyles = `
.ripple {
  position: absolute;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  transform: scale(0);
  animation: ripple 0.6s linear;
  pointer-events: none;
}

@keyframes ripple {
  to {
    transform: scale(4);
    opacity: 0;
  }
}

/* Modern drawer sidebar styles */
.drawer-sidebar {
  background-color: #001B3D;
  box-shadow: 2px 0 10px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 250px;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 50;
  overflow-y: auto;
  overflow-x: hidden;
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
  transition: transform 0.3s ease-in-out;
  transform: translateX(0);
}

.drawer-sidebar.closed {
  transform: translateX(-100%);
}

/* Hide scrollbar for Chrome, Safari and Opera */
.drawer-sidebar::-webkit-scrollbar {
  display: none;
}

/* Custom scrollbar for sidebar */
.simplebar-scrollbar::before {
  background-color: rgba(255, 255, 255, 0.2) !important;
  width: 4px !important;
  border-radius: 4px !important;
}

.simplebar-track.simplebar-vertical {
  width: 6px !important;
  background-color: rgba(0, 0, 0, 0.1) !important;
}

.drawer-sidebar-logo {
  padding: 16px 0;
  margin-bottom: 10px;
  width: 100%;
  display: flex;
  justify-content: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.drawer-sidebar-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}

.drawer-sidebar-divider {
  height: 1px;
  width: 90%;
  margin: 8px auto;
  background-color: rgba(255, 255, 255, 0.1);
}

.drawer-menu-item {
  position: relative;
  padding: 10px 16px;
  margin: 2px 8px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: rgba(255, 255, 255, 0.7);
  transition: all 0.2s ease;
  cursor: pointer;
}

.drawer-menu-item:hover {
  background-color: rgba(59, 130, 246, 0.2);
  color: white;
}

.drawer-menu-item.active {
  background-color: #3b82f6;
  color: white;
}

.drawer-menu-item .icon {
  transition: all 0.2s ease;
}

.drawer-menu-item:hover .icon {
  transform: translateY(-1px);
}

.drawer-menu-item:active .icon {
  transform: translateY(0);
}

.drawer-submenu-item {
  position: relative;
  padding: 8px 16px;
  margin: 2px 8px 2px 24px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.2s ease;
  cursor: pointer;
  font-size: 0.9rem;
}

.drawer-submenu-item:hover {
  background-color: rgba(59, 130, 246, 0.2);
  color: white;
}

.drawer-submenu-item.active {
  background-color: rgba(59, 130, 246, 0.3);
  color: white;
}

.notification-badge {
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background-color: #ef4444;
  color: white;
  font-size: 10px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 4px;
}

.notification-container {
  position: relative;
}

.notification-dropdown {
  position: absolute;
  background-color: #0f172a;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  z-index: 1000;
  border: 1px solid rgba(255, 255, 255, 0.1);
  width: 350px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
}

.notification-header {
  padding: 12px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.notification-title {
  font-weight: 500;
  color: white;
  font-size: 14px;
}

.notification-close {
  color: rgba(255, 255, 255, 0.6);
  cursor: pointer;
  transition: color 0.2s ease;
}

.notification-close:hover {
  color: white;
}

.notification-content {
  overflow-y: auto;
  max-height: calc(80vh - 50px);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 500;
  color: white;
  background-color: #3b82f6;
  border: 2px solid rgba(59, 130, 246, 0.5);
}

.user-avatar-image {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid rgba(59, 130, 246, 0.5);
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background-color: rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.7);
  cursor: pointer;
  transition: all 0.2s ease;
  z-index: 10;
}

.close-button:hover {
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
}

/* Mobile notification dropdown styles */
.notifications-dropdown-mobile {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
}

/* Responsive adjustments for notification dropdown */
@media (max-width: 768px) {
  .notification-dropdown {
    position: fixed;
    top: 50% !important;
    left: 50% !important;
    transform: translate(-50%, -50%) !important;
    width: 90vw;
    max-width: 400px;
    max-height: 80vh;
  }
}
`

const MobileSidebar = () => {
  const { user } = useSelector((state) => state.authenticationReducer)
  const { notificationList } = useSelector((state) => state.commonReducer)
  const { isOpenSidebar } = useSelector((state) => state.eventReducer)
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  // Dropdown states
  const [showNotificationDropdown, setShowNotificationDropdown] = useState(false)
  const notificationDropdownRef = useRef(null)
  const notificationButtonRef = useRef(null)
  const notificationContainerRef = useRef(null)
  const [changeCallHippoPermission, setCallHippoPermission] = useState(false)
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 })

  // Function to close the sidebar
  const toggleSidebar = () => {
    dispatch(handlePopupModalAction({ isOpenSidebar: false }))
  }

  useEffect(() => {
    if (user?.userType !== "MARKETING" && user?.userType !== "ACCOUNTS" && user?.userType !== "HR") {
    dispatch(getNotificationListAction({ userType: user?.userType }))
    }
  }, [user, dispatch])

  // Dynamically calculate sidebar based on user object
  const sideBar = useMemo(() => {
    if (user?.userType === "MANAGER") {
      return DashboardMenuManager(user)
    } else if (user?.userType === "CLIENT") {
      return DashboardMenuClient
    } else if (user?.userType === "OPERATOR") {
      return DashboardMenuOperator(user)
    } else if (user?.userType === "EXPERT") {
      return DashboardMenuExpert
    } else if (user?.userType === "SUPER_ADMIN") {
      return SidebarOptionSuperAdmin(user)
    } else if (user?.userType === "ADMIN") {
      return SidebarOptionAdmin(user)
    } else if (user?.userType === "MARKETING") {
      return SidebarOptionMarketing(user)
    }
    return []
  }, [user])

  // Manage open/close state of menus
  const openMenus = useMemo(() => {
    const currentPath = location.pathname
    const newOpenMenus = {}
    sideBar.forEach((menu) => {
      if (menu.children) {
        const isChildActive = menu.children.some((child) => child.link === currentPath)
        if (isChildActive) {
          newOpenMenus[menu.title] = true
        }
      }
    })
    return newOpenMenus
  }, [location.pathname, sideBar])

  const [menuState, setMenuState] = useState(openMenus)

  useEffect(() => {
    setMenuState(openMenus)
  }, [openMenus])

  // Calculate dropdown position when it's shown
  useEffect(() => {
    if (showNotificationDropdown && notificationButtonRef.current) {
      const buttonRect = notificationButtonRef.current.getBoundingClientRect()
      const isMobile = window.innerWidth <= 768

      if (isMobile) {
        // Mobile positioning is handled by CSS
        return
      }

      if (isOpenSidebar) {
        // When sidebar is open, position to the right of the sidebar
        setDropdownPosition({
          top: buttonRect.top,
          left: 260, // Sidebar width (250px) + 10px margin
        })
      } else {
        // When sidebar is closed, position to the right of the collapsed sidebar
        setDropdownPosition({
          top: buttonRect.top,
          left: 76, // Collapsed sidebar width (66px) + 10px margin
        })
      }
    }
  }, [showNotificationDropdown, isOpenSidebar])

  // Close notification dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        showNotificationDropdown &&
        notificationDropdownRef.current &&
        !notificationDropdownRef.current.contains(event.target) &&
        !notificationButtonRef.current.contains(event.target)
      ) {
        setShowNotificationDropdown(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [showNotificationDropdown])

  const toggleMenu = useCallback((menuTitle) => {
    setMenuState((prev) => ({ ...prev, [menuTitle]: !prev[menuTitle] }))
  }, [])

  const handleNavigation = useCallback(
    (link, menuTitle) => {
      navigate(link)
      if (menuTitle) {
        setMenuState((prev) => ({ ...prev, [menuTitle]: true }))
      }
      toggleSidebar()
    },
    [navigate],
  )

  const getRelevantIcon = useCallback((menuTitle) => {
    // Icon props
    const IconProps = {
      className: "icon",
      size: 20,
      strokeWidth: 1.75,
    }

    switch (menuTitle.toLowerCase()) {
      case "dashboard":
        return <LayoutDashboard {...IconProps} />
      case "users":
        return <Users {...IconProps} />
      case "leads & order":
        return <LogIn {...IconProps} />
      case "analytics":
        return <BarChart2 {...IconProps} />
      case "orders":
        return <ShoppingCart {...IconProps} />
      case "subscribers":
        return <UserPlus {...IconProps} />
      case "payouts":
        return <CreditCard {...IconProps} />
      case "posts":
        return <FileText {...IconProps} />
      case "logout":
        return <LogOut {...IconProps} />
      case "live users":
        return <Activity {...IconProps} />
      case "reports":
        return <BarChart2 {...IconProps} />
      case "queries":
        return <Headset {...IconProps} />
      case "android app panel":
        return <MonitorIcon {...IconProps} />
      case "share":
        return <Share2 {...IconProps} />
      case "pricing":
        return <DollarSign {...IconProps} />
      case "form leads":
        return <Headset {...IconProps} />
      case "whatsapp":
        return <Whatsapp size={20} style={{ fill: "currentColor" }} className="icon" />
      default:
        return <Headset {...IconProps} />
    }
  }, [])

  // Get user initials for avatar fallback
  const getUserInitials = () => {
    if (!user?.name) return "U"
    return user.name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
  }

  // Check if a menu item is active
  const isMenuActive = useCallback(
    (menu) => {
      if (menu.link === location.pathname) return true
      if (menu.children) {
        return menu.children.some((child) => child.link === location.pathname)
      }
      return false
    },
    [location.pathname],
  )

  // Add style tag for custom styles
  useEffect(() => {
    const styleElement = document.createElement("style")
    styleElement.innerHTML = customStyles
    document.head.appendChild(styleElement)

    return () => {
      document.head.removeChild(styleElement)
    }
  }, [])

  // Create ripple effect function
  const createRipple = (e) => {
    const button = e.currentTarget
    const circle = document.createElement("span")
    const diameter = Math.max(button.clientWidth, button.clientHeight)

    circle.style.width = circle.style.height = `${diameter}px`
    circle.style.left = `${e.clientX - button.offsetLeft - diameter / 2}px`
    circle.style.top = `${e.clientY - button.offsetTop - diameter / 2}px`
    circle.classList.add("ripple")

    const ripple = button.getElementsByClassName("ripple")[0]
    if (ripple) {
      ripple.remove()
    }

    button.appendChild(circle)
  }

  // Toggle notification dropdown
  const toggleNotificationDropdown = (e) => {
    if (e) {
      e.stopPropagation()
      createRipple(e)
    }
    setShowNotificationDropdown(!showNotificationDropdown)
  }

  // Close notification dropdown explicitly
  const closeNotificationDropdown = (e) => {
    if (e) {
      e.stopPropagation()
    }
    setShowNotificationDropdown(false)
  }

    const logout = () => {
      dispatch(closeChatAction())
      dispatch(logoutAction({
        cb:()=>navigate("/")
      }));
      
    };

  return (
    <>
      <CustomModal
        open={changeCallHippoPermission}
        size="md"
        title={"Change Permission"}
        content={() => <FormCallHippoPermission />}
        onClose={() => setCallHippoPermission(false)}
      />

      <div className={`drawer-sidebar ${!isOpenSidebar ? "closed" : ""}`}>
        <button className="close-button" onClick={toggleSidebar} aria-label="Close sidebar">
          <X size={18} />
        </button>
        <SimpleBar className="h-screen relative">
          <nav className="py-4 flex flex-col h-full">
            <div className="px-2 mb-6 mt-4">
              <span
                onClick={() => checkLogin(navigate, user)}
                className="flex items-center justify-center cursor-pointer"
              >
                <img src="/assets/images/logo.png" alt="Clickinpedia" className="h-10 w-auto filter invert" />
              </span>
            </div>

            {/* User Profile Section */}
            <div className="px-2 mb-6">
              <div className="bg-[#002a5a] rounded-lg p-3 flex items-center">
                {user?.profilePic ? (
                  <img
                    src={user.profilePic || "/placeholder.svg"}
                    alt={user.name || "User"}
                    className="h-10 w-10 rounded-full object-cover border-2 border-blue-400"
                  />
                ) : (
                  <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center border-2 border-blue-400">
                    {getUserInitials()}
                  </div>
                )}
                <div className="ml-3 flex-1 min-w-0">
                  <p className="text-white font-medium truncate">{user?.name || "User"}</p>
                  <p className="text-gray-400 text-xs truncate">{user?.email || user?.userType}</p>
                </div>
              </div>
            </div>

            {/* Notification Section */}
            {user?.userType !== "MARKETING" && (
              <div className="px-2 mb-4 relative notification-container" ref={notificationContainerRef}>
                <button
                  ref={notificationButtonRef}
                  className="w-full bg-[#002a5a] hover:bg-[#003a7a] rounded-lg p-2.5 flex items-center justify-between text-white transition-all hover:shadow-md active:shadow-sm relative overflow-hidden"
                  onClick={(e) => {
                    e.stopPropagation()
                    toggleNotificationDropdown(e)
                  }}
                >
                  <div className="flex items-center">
                    <Bell size={18} className="mr-2" />
                    <span className="text-sm font-medium">Notifications</span>
                  </div>
                  {notificationList?.newCount > 0 && (
                    <span className="bg-red-500 text-white text-xs font-medium px-2 py-0.5 rounded-full animate-pulse">
                      {notificationList?.newCount}
                    </span>
                  )}
                </button>
              </div>
            )}

            {/* Main Menu */}
            <ul className="space-y-0.5 flex-1 overflow-y-auto px-2">
              {sideBar.map((menu, index) => (
                <li key={index}>
                  <div
                    className={`drawer-menu-item ${isMenuActive(menu) ? "active" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation()
                      createRipple(e)
                      if (menu.children) {
                        toggleMenu(menu.title)
                      } else if (menu.link) {
                        handleNavigation(menu.link)
                        menu?.callFunction && menu?.callFunction()
                      }
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      {getRelevantIcon(menu.title)}
                      <span className="font-medium" onClick={() => menu?.callFunction && menu?.callFunction()}>
                        {menu.title}
                      </span>
                    </div>
                    {menu.children &&
                      (menuState[menu.title] ? (
                        <ChevronUp className="text-sm text-white" size={18} />
                      ) : (
                        <ChevronDown className="text-sm text-white" size={18} />
                      ))}
                  </div>
                  {menu.children && menuState[menu.title] && (
                    <ul className="mt-1 space-y-0.5 mb-2">
                      {menu.children.map((child, childIndex) => (
                        <li key={childIndex}>
                          <div
                            className={`drawer-submenu-item ${child.link === location.pathname ? "active" : ""}`}
                            onClick={(e) => {
                              e.stopPropagation()
                              createRipple(e)
                              handleNavigation(child.link, menu.title)
                            }}
                          >
                            {child.name}
                          </div>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>

            {/* User Settings Section */}
            {(user?.userType === "MANAGER" || user?.userType === "ADMIN" || user?.userType === "SUPER_ADMIN") && (
              <div className="px-2 py-2 mt-auto">
                <button
                  onClick={() => setCallHippoPermission(true)}
                  className="w-full flex items-center px-3 py-2.5 rounded-md text-gray-300 hover:bg-[#002a5a] hover:text-white transition-all hover:shadow-md active:shadow-sm relative overflow-hidden"
                >
                  <div className="flex items-center space-x-3">
                    <Settings className="text-white" size={20} strokeWidth={1.75} />
                    <span className="font-medium">Change Permission</span>
                  </div>
                </button>
              </div>
            )}

            {/* Logout Button */}
            <div className="px-2 py-4 mt-auto border-t border-gray-700/50" onClick={logout}>
              <div className="w-full flex items-center justify-between px-3 py-2.5 rounded-md text-gray-300 hover:bg-[#002a5a] hover:text-white transition-all hover:shadow-md active:shadow-sm relative overflow-hidden">
                <div className="flex items-center space-x-3">
                  <LogOut className="text-white" size={22} strokeWidth={1.75} />
                  <Logout child={"Logout"} />
                </div>
              </div>
            </div>
          </nav>
        </SimpleBar>
      </div>

      {/* Notification Dropdown - Positioned outside the sidebar with dynamic positioning */}
      {showNotificationDropdown && (
        <div
          ref={notificationDropdownRef}
          className="notification-dropdown"
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
          }}
        >
          <div className="notification-header">
            <div className="notification-title">Notifications</div>
            <div className="flex items-center">
              <Link
                to="/"
                className="text-xs text-blue-400 hover:text-blue-300 mr-3"
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                Settings
              </Link>
              <button onClick={closeNotificationDropdown} className="notification-close">
                <X size={16} />
              </button>
            </div>
          </div>
          <div className="notification-content">
            <NotificationList />
          </div>
        </div>
      )}
    </>
  )
}

export default React.memo(MobileSidebar)

